import { Component, DestroyRef, Input, OnChanges, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { HelpersService } from '../../../../services/helpers.service';
import { NavService } from '../../../../services/nav.service';
import { NotificationService } from '../../../../services/notification.service';
import { ToastService } from '@app/services/toast.service';
import * as Global from '../../../../global';

type NotificationFilter = 'all' | 'unread';
type NotificationCategory = 'trip' | 'expense' | 'general';
type NotificationTone = 'success' | 'error' | 'warning' | 'info' | 'neutral';

/** View model built once per fetch so the template does no string parsing. */
interface NotificationView {
  raw: any;
  id: any;
  read: boolean;
  actorName: string;
  initials: string;
  avatarUrl: string | null;
  avatarColor: string;
  category: NotificationCategory;
  categoryIcon: string;
  /** true when the content matched CONTENT_PATTERN */
  structured: boolean;
  /** Sentence after the actor, e.g. "applied for trip" — raw content when unstructured */
  action: string;
  categoryLabel: string;
  /** e.g. "TR#0000742" */
  reference: string | null;
  status: string | null;
  tone: NotificationTone;
  date: Date | null;
  relativeTime: string;
  clickable: boolean;
}

interface NotificationGroup {
  label: string;
  items: NotificationView[];
}

// "Trip APPLIED - Trip No. TR#0000742" / "Expense added - Expense No. EX#00003998"
const CONTENT_PATTERN = /^(trip|expense)\s+(.+?)\s*-\s*.*?no\.?\s*(\S+)\s*$/i;

const STATUS_TONES: Record<string, NotificationTone> = {
  approved: 'success',
  settled: 'success',
  paid: 'success',
  completed: 'success',
  rejected: 'error',
  cancelled: 'error',
  canceled: 'error',
  pending: 'warning',
  'sent back': 'warning',
  returned: 'warning',
  applied: 'info',
  added: 'info',
  submitted: 'info',
  updated: 'info',
};

// Status → verb phrase used to build "Chandan Mandal applied for trip TR#…"
const STATUS_VERBS: Record<string, string> = {
  applied: 'applied for',
  pending: 'has a pending',
  'sent back': 'sent back',
};

const AVATAR_COLORS = ['#3d5af1', '#0ea5e9', '#14b8a6', '#8b5cf6', '#f97316', '#ec4899', '#64748b'];

@Component({
  selector: 'notifications',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, MatTooltipModule, NgxSkeletonLoaderModule, DatePipe],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss',
})
export class NotificationsComponent implements OnInit, OnChanges {
  @Input() data: any = null;

  private notification = inject(NotificationService);
  private helpers = inject(HelpersService);
  private toastr = inject(ToastService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  navService = inject(NavService);

  isLoading = false;
  hasError = false;
  isMarkingAll = false;
  filter: NotificationFilter = 'all';

  items: NotificationView[] = [];
  groups: NotificationGroup[] = [];

  ngOnInit(): void {
    // Live refresh while the panel is open (push received in foreground/background)
    this.notification
      .onChanged()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.getNotifications(true));
  }

  ngOnChanges(): void {
    this.getNotifications();
  }

  get unreadCount(): number {
    return this.items.filter((item) => !item.read).length;
  }

  setFilter(filter: NotificationFilter): void {
    this.filter = filter;
    this.buildGroups();
  }

  close(): void {
    this.navService.openSideNav({ toogle: true });
  }

  /** `silent` refreshes in place without flashing the skeleton loader. */
  getNotifications(silent = false): void {
    if (!silent) this.isLoading = true;
    this.hasError = false;

    this.notification.getNotifications({ userid: this.helpers.userId() }).subscribe({
      next: (res) => {
        const list: any[] = res?.data ?? [];
        this.items = list.map((item) => this.toView(item));
        this.buildGroups();
        this.isLoading = false;
        this.fetchNotificationCount();
      },
      error: () => {
        this.isLoading = false;
        if (!silent) this.hasError = true;
      },
    });
  }

  open(item: NotificationView): void {
    if (!item.read) this.markAsRead(item);
    if (item.clickable) {
      this.close();
      this.router.navigateByUrl(item.raw.redirection);
    }
  }

  /** Optimistic: update the UI immediately, roll back if the API fails. */
  markAsRead(item: NotificationView, event?: Event): void {
    event?.stopPropagation();
    if (item.read) return;

    this.setRead([item], true);
    this.notification.notificationMarkAsRead({ id: item.id }).subscribe({
      next: () => this.fetchNotificationCount(),
      error: () => this.setRead([item], false),
    });
  }

  markAllAsRead(): void {
    const unread = this.items.filter((item) => !item.read);
    if (!unread.length || this.isMarkingAll) return;

    this.isMarkingAll = true;
    this.setRead(unread, true);
    this.notification.notificationMarkAllAsRead({}).subscribe({
      next: () => {
        this.isMarkingAll = false;
        this.fetchNotificationCount();
      },
      error: () => {
        this.isMarkingAll = false;
        this.setRead(unread, false);
        this.toastr.error('Could not mark notifications as read. Please try again.');
      },
    });
  }

  trackById = (_: number, item: NotificationView) => item.id;

  onAvatarError(item: NotificationView): void {
    item.avatarUrl = null;
  }

  private setRead(targets: NotificationView[], read: boolean): void {
    targets.forEach((item) => {
      item.read = read;
      item.raw.read = read;
    });
    this.buildGroups();
  }

  private fetchNotificationCount(): void {
    this.notification.getNotificationsCount({ userid: this.helpers.userId() }).subscribe({
      next: (res) => this.notification.setCount(res?.data ?? null),
      error: () => {},
    });
  }

  private buildGroups(): void {
    const visible = this.filter === 'unread' ? this.items.filter((item) => !item.read) : this.items;

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfWeek.getDate() - 6);

    const buckets: NotificationGroup[] = [
      { label: 'Today', items: [] },
      { label: 'Yesterday', items: [] },
      { label: 'This week', items: [] },
      { label: 'Earlier', items: [] },
    ];

    visible.forEach((item) => {
      const time = item.date?.getTime() ?? 0;
      if (time >= startOfToday.getTime()) buckets[0].items.push(item);
      else if (time >= startOfYesterday.getTime()) buckets[1].items.push(item);
      else if (time >= startOfWeek.getTime()) buckets[2].items.push(item);
      else buckets[3].items.push(item);
    });

    this.groups = buckets.filter((group) => group.items.length);
  }

  private toView(item: any): NotificationView {
    const content: string = (item?.content ?? '').trim();
    const firstName = item?.sourceUser?.firstName ?? '';
    const lastName = item?.sourceUser?.lastName ?? '';
    const actorName = `${firstName} ${lastName}`.trim() || 'System';

    const match = content.match(CONTENT_PATTERN);
    const category: NotificationCategory = match
      ? (match[1].toLowerCase() as NotificationCategory)
      : /trip/i.test(content)
        ? 'trip'
        : /expense/i.test(content)
          ? 'expense'
          : 'general';

    const status = match ? match[2].trim().toLowerCase() : this.detectStatus(content);
    const action = match ? `${STATUS_VERBS[status!] ?? status} ${category}` : content;
    const date = item?.createdDate ? new Date(Global.formatDateToUTC(item.createdDate)) : null;

    return {
      raw: item,
      id: item?.id,
      read: !!item?.read,
      actorName,
      initials: this.initials(firstName, lastName, actorName),
      avatarUrl: item?.sourceUser?.profilePhoto
        ? `${Global.BACKEND_URL}/users/${item.sourceUser.profilePhoto}`
        : null,
      avatarColor: this.colorFor(actorName),
      category,
      categoryIcon: category === 'trip' ? 'flight_takeoff' : category === 'expense' ? 'receipt_long' : 'notifications',
      structured: !!match,
      action,
      categoryLabel: category === 'general' ? 'General' : this.capitalize(category),
      reference: match ? match[3] : null,
      status,
      tone: (status && STATUS_TONES[status]) || 'neutral',
      date: date && !isNaN(date.getTime()) ? date : null,
      relativeTime: this.relativeTime(date),
      clickable: !!item?.redirection,
    };
  }

  private detectStatus(content: string): string | null {
    const lower = content.toLowerCase();
    return Object.keys(STATUS_TONES).find((key) => lower.includes(key)) ?? null;
  }

  private relativeTime(date: Date | null): string {
    if (!date || isNaN(date.getTime())) return '';
    const diffSec = Math.round((Date.now() - date.getTime()) / 1000);
    if (diffSec < 60) return 'Just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  }

  private initials(first: string, last: string, fallback: string): string {
    const value = `${first.charAt(0)}${last.charAt(0)}`.trim() || fallback.charAt(0);
    return value.toUpperCase();
  }

  private colorFor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  }

  private capitalize(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }
}
